/**
 * Het tabblad Ontwikkeling: links de groepen, en na een klik de leerlingen
 * van die groep (met hun start- en huidige niveau); rechts de groeicurve van
 * de gekozen leerling (onderdelen/Groeicurve.js), en wanneer de leerkracht
 * zijn niveau veranderde (stap 17e). Sinds stap 24 staan die beslissingen
 * en de papieren toetsen ook in de curve en in de geschiedenis.
 *
 * De gekozen leerling wordt onthouden bij zijn id, niet als kopie: is hij
 * intussen verwijderd (tabblad Groepen), dan staat hij hier na het opnieuw
 * ophalen ook niet meer.
 */
import React, { useEffect, useState } from 'react';
import { leerkrachtApi as api } from '../../../api/leerkracht';
import { Groeicurve } from '../onderdelen/Groeicurve';
import { perGroep } from '../perGroep';

/** "2026-10-23T21:04:00" → "23-10-2026". */
const datum = (iso) => (iso ? iso.slice(0, 10).split('-').reverse().join('-') : '');

export function Ontwikkeling({ actief, meldFout }) {
  const [leerlingen, setLeerlingen] = useState([]);
  const [laden, setLaden] = useState(false);
  const [groep, setGroep] = useState(null);
  const [leerlingId, setLeerlingId] = useState(null);

  useEffect(() => {
    if (!actief) return;
    setLaden(true);
    api.haalOntwikkeling().then(setLeerlingen).catch(err => meldFout(err.message)).finally(() => setLaden(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen van het tabblad
  }, [actief]);

  if (laden) return <p>Laden...</p>;

  const gekozen = leerlingen.find(l => l.student_id === leerlingId) || null;

  return (
    <div className="grid-2">
      <div className="card">
        <h2 className="mb-16">Groepen</h2>
        {leerlingen.length === 0 ? (
          <p className="text-muted">Geen leerlingen gevonden</p>
        ) : (
          <>
            <div style={{ marginBottom: '16px' }}>
              {perGroep(leerlingen).map(g => (
                <div
                  key={g.class_id}
                  className="card"
                  style={{ cursor: 'pointer', background: groep === g.class_id ? 'var(--blauw-vlak)' : 'var(--wit)', marginBottom: 8, padding: '12px 16px' }}
                  onClick={() => { setGroep(groep === g.class_id ? null : g.class_id); setLeerlingId(null); }}
                >
                  <div className="flex-between">
                    <strong>{g.class_naam}</strong>
                    <span className="badge badge-primary">{g.items.length} leerling{g.items.length !== 1 ? 'en' : ''}</span>
                  </div>
                </div>
              ))}
            </div>

            {groep && (
              <>
                <h3 className="mb-8" style={{ marginTop: '24px', borderTop: '1px solid var(--lijn-4)', paddingTop: '16px' }}>Leerlingen</h3>
                <div>
                  {leerlingen.filter(l => l.class_id === groep).map(l => (
                    <div
                      key={l.student_id}
                      className="card"
                      style={{ cursor: 'pointer', background: leerlingId === l.student_id ? 'var(--goed-vlak)' : 'var(--vlak)', marginBottom: 8, padding: '12px 16px' }}
                      onClick={() => setLeerlingId(l.student_id)}
                    >
                      <div className="flex-between">
                        <div>
                          <strong>{l.student_naam}</strong>
                          <div className="text-muted" style={{ fontSize: '13px', marginTop: 4 }}>
                            Start: {l.start_niveau || '-'} → Huidig: {l.huidig_niveau || '-'}
                          </div>
                        </div>
                        <div><span className="badge badge-primary">{l.datapunten.length} toetsen</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="card">
        {!gekozen ? (
          <p className="text-muted text-center">Selecteer een leerling om de ontwikkeling te bekijken</p>
        ) : (
          <>
            <h2 className="mb-8">{gekozen.student_naam}</h2>
            <p className="text-muted mb-8">
              Startniveau: <strong>{gekozen.start_niveau || '-'}</strong> |
              Huidig niveau: <strong>{gekozen.huidig_niveau || '-'}</strong>
              {gekozen.laatst_gelezen_niveau && gekozen.laatst_gelezen_niveau !== gekozen.huidig_niveau && (
                <> | Laatste les gelezen op: <strong>{gekozen.laatst_gelezen_niveau}</strong></>
              )}
            </p>
            {(gekozen.niveauwijzigingen || []).length > 0 && (
              <p className="text-muted text-small mb-16" data-testid="niveauwijzigingen">
                Niveau veranderd: {gekozen.niveauwijzigingen.map(w => `${datum(w.datum)} ${w.van || '-'} → ${w.naar}${w.door ? ` (${w.door})` : ''}`).join('; ')}.
                Een toets op een hoger niveau gaat over een moeilijkere tekst: de scores ervoor en erna zijn niet zomaar te vergelijken.
              </p>
            )}
            {gekozen.datapunten.length === 0 && <p className="text-muted">Nog geen toetsresultaten in de app</p>}
            <Groeicurve leerling={gekozen} />
          </>
        )}
      </div>
    </div>
  );
}
